// components/BookingDetails/ClientCard.jsx
import React from "react";
import { FiPhone, FiMail, FiMapPin, FiUser, FiSearch } from "react-icons/fi";
import { FaDirections } from "react-icons/fa";
import Swal from "sweetalert2";
import { MdHistory } from "react-icons/md";

const ClientCard = ({
  clientData,
  onViewEnquiry,
  onViewHistory,
  showViewEnquiryButton = true,
  showViewHistoryButton = true,
  showDirectionsButton = true,
}) => {
  if (!clientData) return null;

  const handleDirectionsClick = () => {
    const addressString = [
      clientData.address?.address1,
      clientData.address?.city,
      clientData.address?.state,
      clientData.address?.zip,
    ]
      .filter(Boolean)
      .join(", ");

    const destination =
      clientData.address?.latitude && clientData.address?.longitude
        ? `${clientData.address.latitude},${clientData.address.longitude}`
        : encodeURIComponent(addressString);

    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;

    Swal.fire({
      title: "Get Directions",
      text: "Do you want to open Google Maps for directions?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(googleMapsUrl, "_blank");
      }
    });
  };

  return (
    <div className="relative p-3 bg-white rounded-xl border border-gray-200 shadow-sm mb-3">
      {/* Recent Visits and Enquiry Buttons - Top Right */}
      <div className="absolute top-3 right-3 flex gap-2">
        {showViewEnquiryButton && (
          <button
            onClick={onViewEnquiry}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-500 text-white text-xs font-medium rounded-lg hover:bg-purple-600 transition-all duration-200"
          >
            <FiSearch className="w-3 h-3" />
            View Enquiry
          </button>
        )}
        {showViewHistoryButton && (
          <button
            onClick={onViewHistory}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 transition-all duration-200"
          >
            <MdHistory className="w-3 h-3" />
            Recent Visits
          </button>
        )}
      </div>

      <div className="space-y-3">
        {/* Client Name */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
            <FiUser className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {clientData.firstname} {clientData.lastname}
            </h3>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <FiPhone className="w-3 h-3" />
                {clientData.phone}
              </span>
              {clientData.email && (
                <span className="flex items-center gap-1">
                  <FiMail className="w-3 h-3" />
                  {clientData.email}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Address Display */}
        {clientData.address?.address1 && (
          <div className="flex items-center gap-2 text-sm text-gray-600 pl-16">
            <FiMapPin className="w-3 h-3" />
            <span>
              {[
                clientData.address.address1,
                clientData.address.city,
                clientData.address.state,
                clientData.address.zip,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
            {showDirectionsButton && (
              <button
                onClick={handleDirectionsClick}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-all duration-200 shadow hover:shadow-md flex-shrink-0"
                title="Get Directions"
              >
                <FaDirections className="w-3 h-3" />
                Directions
              </button>
            )}
          </div>
        )}

        {/* Labels Display */}
        {clientData.labels &&
          clientData.labels.length > 0 &&
          clientData.labels.some(
            (labelString) => labelString && labelString.trim() !== "",
          ) && (
            <div className="flex items-center gap-2 pl-16">
              <div className="flex flex-wrap gap-2">
                {clientData.labels.map((labelString, index) => {
                  const individualLabels = labelString
                    .split(",")
                    .map((label) => label.trim());
                  return individualLabels.map((label, labelIndex) => {
                    if (!label || label.trim() === "") return null;
                    return (
                      <span
                        key={`${index}-${labelIndex}`}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                      >
                        {label}
                      </span>
                    );
                  });
                })}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ClientCard;
